import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Buildings, Plus, SignOut, Pencil, Trash, Users } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FleetsDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [fleets, setFleets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFleet, setEditingFleet] = useState(null);
  const [newFleet, setNewFleet] = useState({ name: '', description: '' });

  const fetchFleets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/fleets`, { withCredentials: true });
      setFleets(data);
    } catch (error) {
      toast.error('Failed to load fleets');
    } finally {
      setLoading(false);
    }
  }, [setFleets, setLoading]);

  useEffect(() => {
    fetchFleets();
  }, [fetchFleets]);

  const handleCreateFleet = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/fleets`, newFleet, { withCredentials: true });
      toast.success('Fleet created successfully');
      setNewFleet({ name: '', description: '' });
      setShowAddModal(false);
      fetchFleets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fleet');
    }
  };

  const handleUpdateFleet = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/fleets/${editingFleet.id}`, 
        { name: editingFleet.name, description: editingFleet.description },
        { withCredentials: true }
      );
      toast.success('Fleet updated successfully');
      setEditingFleet(null);
      fetchFleets();
    } catch (error) {
      toast.error('Failed to update fleet');
    }
  };

  const handleDeleteFleet = async (fleetId) => {
    if (!window.confirm('Are you sure? This will delete all crew members and schedules in this fleet.')) return;
    try {
      await axios.delete(`${API_URL}/api/fleets/${fleetId}`, { withCredentials: true });
      toast.success('Fleet deleted');
      fetchFleets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete fleet');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-[#4B5563]">Loading fleets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Buildings size={32} weight="duotone" className="text-[#002FA7]" />
              <div>
                <h1 className="text-2xl font-bold text-[#111827] tracking-tight">OmniLink Fleet Manager</h1>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-[0.2em]">Operations Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-[#4B5563]">{user?.name}</span>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#4B5563] hover:text-[#002FA7] hover:bg-gray-50 rounded-sm transition-colors"
              >
                <SignOut size={20} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-[#111827]">Fleet Management</h2>
            <p className="text-sm text-[#6B7280] mt-1">Manage your fleet operations and crew schedules</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="add-fleet-button"
            className="flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white rounded-sm font-medium hover:bg-[#001f7a] transition-colors"
          >
            <Plus size={20} weight="bold" />
            Add Fleet
          </button>
        </div>

        {fleets.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-12 text-center">
            <Buildings size={64} className="mx-auto text-[#9CA3AF] mb-4" weight="duotone" />
            <h3 className="text-lg font-semibold text-[#111827] mb-2">No Fleets Yet</h3>
            <p className="text-sm text-[#6B7280] mb-4">Get started by creating your first fleet</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-[#002FA7] text-white rounded-sm font-medium hover:bg-[#001f7a] transition-colors"
            >
              Create Fleet
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fleets.map((fleet) => (
              <div
                key={fleet.id}
                data-testid={`fleet-card-${fleet.id}`}
                className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#111827] mb-2">{fleet.name}</h3>
                      <p className="text-sm text-[#6B7280]">{fleet.description || 'No description'}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingFleet(fleet);
                        }}
                        data-testid={`edit-fleet-${fleet.id}`}
                        className="p-2 text-[#6B7280] hover:text-[#002FA7] hover:bg-gray-50 rounded-sm transition-colors"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFleet(fleet.id);
                        }}
                        data-testid={`delete-fleet-${fleet.id}`}
                        className="p-2 text-[#6B7280] hover:text-[#FF2A2A] hover:bg-gray-50 rounded-sm transition-colors"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="border-t border-[#E5E7EB] pt-4">
                    <button
                      onClick={() => navigate(`/fleet/${fleet.id}/crew`)}
                      data-testid={`manage-crew-${fleet.id}`}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#F3F4F6] text-[#111827] rounded-sm font-medium hover:bg-[#E5E7EB] transition-colors mb-2"
                    >
                      <Users size={18} />
                      Manage Crew
                    </button>
                    <button
                      onClick={() => navigate(`/fleet/${fleet.id}/schedule`)}
                      data-testid={`view-schedule-${fleet.id}`}
                      className="w-full px-4 py-2 bg-[#002FA7] text-white rounded-sm font-medium hover:bg-[#001f7a] transition-colors"
                    >
                      View Schedule
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="add-fleet-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#111827] mb-4">Create New Fleet</h3>
            <form onSubmit={handleCreateFleet}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#111827] mb-2">Fleet Name</label>
                <input
                  type="text"
                  data-testid="fleet-name-input"
                  placeholder="e.g., A318, A340, Cockpit"
                  value={newFleet.name}
                  onChange={(e) => setNewFleet({ ...newFleet, name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#111827] mb-2">Description</label>
                <textarea
                  data-testid="fleet-description-input"
                  placeholder="Optional description"
                  value={newFleet.description}
                  onChange={(e) => setNewFleet({ ...newFleet, description: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewFleet({ name: '', description: '' });
                  }}
                  data-testid="cancel-add-fleet"
                  className="px-4 py-2 border border-[#E5E7EB] text-[#4B5563] rounded-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-add-fleet"
                  className="px-4 py-2 bg-[#002FA7] text-white rounded-sm font-medium hover:bg-[#001f7a] transition-colors"
                >
                  Create Fleet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingFleet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="edit-fleet-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#111827] mb-4">Edit Fleet</h3>
            <form onSubmit={handleUpdateFleet}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#111827] mb-2">Fleet Name</label>
                <input
                  type="text"
                  data-testid="edit-fleet-name-input"
                  value={editingFleet.name}
                  onChange={(e) => setEditingFleet({ ...editingFleet, name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#111827] mb-2">Description</label>
                <textarea
                  data-testid="edit-fleet-description-input"
                  value={editingFleet.description || ''}
                  onChange={(e) => setEditingFleet({ ...editingFleet, description: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingFleet(null)}
                  data-testid="cancel-edit-fleet"
                  className="px-4 py-2 border border-[#E5E7EB] text-[#4B5563] rounded-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-edit-fleet"
                  className="px-4 py-2 bg-[#002FA7] text-white rounded-sm font-medium hover:bg-[#001f7a] transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}